import axios, { AxiosError } from "axios";
import {
  CredentialsType,
  DefaultReponseType,
  EnableUserResponseType,
  LoginResponseType,
  ProfileListResponseType,
  RoleListResponseType,
  SendPasswordRecoveryEmailType,
  UserInviteType,
  UserProfile,
} from "../global/types/user";
import {
  TokenRefreshResult,
  TokenRefreshErrorType,
} from "../global/types/auth";
import {
  ForwardGeocodingRequest,
  GeocodingFeature,
  ReverseGeocodingRequest,
  ReverseGeocodingResponse,
} from "../global/types/geocoding";
import {
  API_PROFILE_ROUTE,
  API_ROLES_ROUTE,
  API_USER_CHANGE_PASSWORD_ROUTE,
  API_USER_ENABLE_ROUTE,
  API_USER_INVITATE_ROUTE,
  API_USER_ROUTE,
  API_USER_SEND_EMAIL_CHANGE_PASSWORD_ROUTE,
  API_GEOCODING_FORWARD_ROUTE,
  API_GEOCODING_REVERSE_ROUTE,
  API_AUTH_LOGIN_ROUTE,
  API_AUTH_REFRESH_ROUTE,
  API_AUTH_LOGOUT_ROUTE,
  API_SOLAR_REPORT_ROUTE,
  API_SOLAR_DISTRIBUTION_ROUTE,
  API_DISTANCE_TO_LAYER_ROUTE,
  API_STATE_STATS_ROUTE,
  API_WIND_SERIES_ROUTE,
  API_WIND_DENSITY_ROUTE,
  API_WIND_DISTRIBUTION_ROUTE,
  API_WIND_POTENTIAL_ROUTE,
  API_WIND_CUMULATIVE_ROUTE,
  API_AREA_POTENTIAL_ROUTE,
  API_AREA_WIND_POTENTIAL_ROUTE,
  API_WIND_ROSE_ROUTE,
  API_REPORT_RASTER_STATS_ROUTE,
  API_REPORT_REGION_AREA_ROUTE,
  API_GENERATION_POWER_SUMMARY_ROUTE,
  API_PROJECTION_AREA_STATS_ROUTE,
  API_RESTRICTION_BREAKDOWN_ROUTE,
  API_REPORT_LEGEND_COUNTS_ROUTE,
  API_RESOURCE_CHARACTERIZATION_ROUTE,
} from "../global/constants/routes/apiConstants";
import {
  getFormattedAuthorizationToken,
  getRefreshToken,
  setAuthorizationToken,
  setRefreshToken,
  unsetRefreshToken,
} from "../global/storage/auth";
import { validateToken } from "../utils/tokenValidation";
import { GetWindRoseParams, WindRose } from "@/types/wind";

export const BASE_URL_API =
  process.env.REACT_APP_URL_API || "https://api-dev-atlas.isitics.com/";

export const apiService = axios.create({ baseURL: BASE_URL_API });
export interface SolarReportPayload {
  layer_name: string;
  year?: number;
  scenario_id?: string;
  geometry?: GeoJSON.Geometry | null;
}

export interface VariableOutput {
  id: string;
  label: string;
  unit: string;
  min: number;
  max: number;
  mean: number;
  monthly_data: number[];
}

export interface SolarReportResponse {
  metadata: any;
  climate_data: VariableOutput[];
}

export const ApiGetSolarReport = async (
  payload: SolarReportPayload
): Promise<SolarReportResponse> => {
  try {
    const response = await apiService.post(API_SOLAR_REPORT_ROUTE, payload);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

apiService.interceptors.request.use(
  (config) => {
    const token = getFormattedAuthorizationToken();
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiService.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiService(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResult = await ApiRefreshToken();

        if (refreshResult.success && refreshResult.data) {
          processQueue(null, refreshResult.data.access_token);
          originalRequest.headers.Authorization = `Bearer ${refreshResult.data.access_token}`;
          return apiService(originalRequest);
        } else {
          console.error(
            "[API Interceptor] Token refresh failed:",
            refreshResult.error
          );
          processQueue(
            new Error(refreshResult.error?.message || "Token refresh failed"),
            null
          );
          window.location.href = "/";
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error(
          "[API Interceptor] Unexpected error during token refresh:",
          refreshError
        );
        processQueue(refreshError, null);
        window.location.href = "/";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export const handleError = (error: any) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    if (axiosError.response) {
      const errorData = axiosError.response.data;
      return errorData;
    }
  }
  return error;
};

export const ApiGetUser = async () => {
  try {
    const response = await apiService.get(API_USER_ROUTE);
    const data = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiSendPasswordRecoveryEmail = async (
  values: SendPasswordRecoveryEmailType
) => {
  try {
    const response = await apiService.post(
      API_USER_SEND_EMAIL_CHANGE_PASSWORD_ROUTE,
      values
    );
    const data = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiRecoveryPassword = async (
  recoveryToken: string,
  values: { password: string }
) => {
  try {
    const response = await apiService.post(
      API_USER_CHANGE_PASSWORD_ROUTE,
      values,
      {
        headers: {
          Authorization: `Bearer ${recoveryToken}`,
        },
      }
    );
    const data = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiEnableUser = async (
  activationToken: string
): Promise<EnableUserResponseType> => {
  try {
    const response = await apiService.post(API_USER_ENABLE_ROUTE, null, {
      headers: {
        Authorization: `Bearer ${activationToken}`,
      },
    });
    const data = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Login response from backend
 *
 * Returned after successful authentication via ApiLogin.
 * Contains access token, refresh token, and user profile data.
 */
interface AuthLoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: UserProfile;
}

export const ApiLogin = async (
  credentials: CredentialsType,
  login: (token: string, userProfile?: UserProfile | null) => void
): Promise<LoginResponseType> => {
  try {
    const response = await apiService.post<AuthLoginResponse>(
      API_AUTH_LOGIN_ROUTE,
      {
        email: credentials.email,
        password: credentials.password,
      }
    );

    const { access_token, refresh_token, user } = response.data;

    setRefreshToken(refresh_token);
    login(access_token, user);

    const data: LoginResponseType = {
      content: access_token,
      message: "Login realizado com sucesso",
    };
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data?.detail || "Credenciais inválidas";
      throw new Error(errorMessage);
    }
    throw handleError(error);
  }
};

/**
 * Refresh response from backend
 *
 * Returned when refreshing expired access token via ApiRefreshToken.
 * Contains new access token, refresh token, and updated user profile.
 */
interface AuthRefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: UserProfile;
}

export const ApiRefreshToken = async (): Promise<TokenRefreshResult> => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return {
        success: false,
        error: {
          type: TokenRefreshErrorType.NO_REFRESH_TOKEN,
          message: "No refresh token found in storage",
          userMessage: "Sua sessão expirou. Por favor, faça login novamente.",
        },
      };
    }

    const refreshTokenValidation = validateToken(refreshToken);
    if (!refreshTokenValidation.isValid) {
      return {
        success: false,
        error: {
          type: TokenRefreshErrorType.EXPIRED_REFRESH_TOKEN,
          message: "Refresh token is expired or invalid",
          userMessage: "Sua sessão expirou. Por favor, faça login novamente.",
          details: refreshTokenValidation,
        },
      };
    }

    const response = await axios.post<AuthRefreshResponse>(
      // Use backend auth refresh endpoint
      `${BASE_URL_API.replace(/\/$/, "")}${API_AUTH_REFRESH_ROUTE}`,
      { refresh_token: refreshToken }
    );

    const { access_token, refresh_token, user } = response.data;

    if (!access_token || !refresh_token) {
      return {
        success: false,
        error: {
          type: TokenRefreshErrorType.INVALID_RESPONSE,
          message: "Invalid response from server: missing tokens",
          userMessage: "Erro ao processar resposta do servidor.",
          details: response.data,
        },
      };
    }

    setAuthorizationToken(access_token);
    setRefreshToken(refresh_token);

    return {
      success: true,
      data: { access_token, refresh_token, user },
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (!error.response) {
        return {
          success: false,
          error: {
            type: TokenRefreshErrorType.NETWORK_ERROR,
            message: "Network error occurred during token refresh",
            userMessage:
              "Erro de conexão ao renovar sessão. Verifique sua internet.",
            details: error.message,
          },
        };
      }

      // Server returned error response (400, 401, etc.)
      if (error.response.status === 400 || error.response.status === 401) {
        return {
          success: false,
          error: {
            type: TokenRefreshErrorType.EXPIRED_REFRESH_TOKEN,
            message: `Server error: ${error.response.status} - ${
              error.response.data?.detail || "Unknown error"
            }`,
            userMessage: "Sua sessão expirou. Por favor, faça login novamente.",
            details: error.response.data,
          },
        };
      }

      return {
        success: false,
        error: {
          type: TokenRefreshErrorType.KEYCLOAK_ERROR,
          message: `Server error: ${error.response.status} - ${error.response.statusText}`,
          userMessage:
            "Erro ao renovar sessão. Por favor, faça login novamente.",
          details: error.response.data,
        },
      };
    }
    return {
      success: false,
      error: {
        type: TokenRefreshErrorType.KEYCLOAK_ERROR,
        message: `Unknown error: ${error}`,
        userMessage: "Erro inesperado ao renovar sessão.",
        details: error,
      },
    };
  }
};

export const ApiLogout = async () => {
  try {
    // Use backend auth logout endpoint
    const refreshToken = getRefreshToken();

    if (refreshToken) {
      await apiService.post(API_AUTH_LOGOUT_ROUTE, {
        refresh_token: refreshToken,
      });
    }

    // Clear refresh token from storage
    unsetRefreshToken();

    const data: LoginResponseType = {
      content: "",
      message: "Logout realizado com sucesso",
    };
    return data;
  } catch (error) {
    console.error("[ApiLogout] Logout error:", error);
    // Even if backend logout fails, clear local tokens and return success
    unsetRefreshToken();
    return {
      content: "",
      message: "Logout realizado com sucesso",
      success: true,
    };
  }
};

export const ApiGetProfiles = async () => {
  try {
    const response = await apiService.get(API_PROFILE_ROUTE);
    const data: ProfileListResponseType = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiGetRoles = async () => {
  try {
    // Authorization header now added automatically by interceptor
    const response = await apiService.get(API_ROLES_ROUTE);
    const data: RoleListResponseType = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiSendInvite = async (values: UserInviteType) => {
  try {
    const response = await apiService.post(API_USER_INVITATE_ROUTE, values);
    const data: DefaultReponseType = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface Geometry {
  type: string;
  coordinates: number[][][];
}

export interface DistributionReportInput {
  layer_name: string;
  year?: number;
  month: number;
  geometry: Geometry | null;
}

export interface DistributionBin {
  min: number;
  max: number;
  percentage: number;
}

export interface AreaStats {
  min: number;
  max: number;
  mean: number;
  std_dev: number;
}

export interface DistributionReportResponse {
  metadata: any;
  stats: AreaStats;
  distribution: DistributionBin[];
}

export const ApiGetAreaDistribution = async (
  payload: DistributionReportInput
): Promise<DistributionReportResponse> => {
  try {
    const response = await apiService.post(
      API_SOLAR_DISTRIBUTION_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiForwardGeocoding = async (
  values: ForwardGeocodingRequest
): Promise<GeocodingFeature[]> => {
  try {
    const response = await apiService.post(API_GEOCODING_FORWARD_ROUTE, values);
    const data: GeocodingFeature[] = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiReverseGeocoding = async (
  values: ReverseGeocodingRequest
): Promise<ReverseGeocodingResponse> => {
  try {
    const response = await apiService.post(API_GEOCODING_REVERSE_ROUTE, values);
    const data: ReverseGeocodingResponse = response.data;
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface DistanceToLayerRequest {
  geometry: {
    type: string;
    coordinates: number[][] | number[][][];
  };
  layer_name: string;
  cql_filter?: string;
}

export interface NearestFeature {
  feature_id: string;
  distance_m: number;
  distance_km: number;
  properties: {
    nomelongo?: string;
    tensao?: number;
    [key: string]: any;
  };
}

export interface DistanceToLayerResponse {
  nearest_feature: NearestFeature;
  total_features_evaluated: number;
  input_geometry_type: string;
}

export const ApiGetDistanceToLayer = async (
  payload: DistanceToLayerRequest
): Promise<DistanceToLayerResponse> => {
  try {
    const response = await apiService.post(
      API_DISTANCE_TO_LAYER_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface AreaDistancesResponse {
  transmission_existing?: DistanceToLayerResponse;
  transmission_planned?: DistanceToLayerResponse;
  distribution_ssdat?: DistanceToLayerResponse;
  distribution_ssdmt?: DistanceToLayerResponse;
  substation_existing?: DistanceToLayerResponse;
  substation_planned?: DistanceToLayerResponse;
}

export const ApiGetAreaDistances = async (payload: {
  geometry: any;
  max_features?: number;
  search_radius_km?: number;
}): Promise<AreaDistancesResponse> => {
  try {
    const response = await apiService.post(
      "/api/v1/geo/area-distances",
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface StatCard {
  label: string;
  value: string;
}

export interface StateStatsResponse {
  energy_type: string;
  stats: StatCard[];
}

export const ApiGetStateStats = async (
  energyType: string = "solar"
): Promise<StateStatsResponse> => {
  try {
    const response = await apiService.get(API_STATE_STATS_ROUTE, {
      params: { energy_type: energyType },
    });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Wind series data response from backend
 *
 * Returns monthly wind velocity data with pre-calculated annual mean.
 * Used by WindChart component to display velocity trends.
 *
 * Backend calculates annual_mean to ensure consistency across all visualizations.
 */
export interface WindSeriesResponse {
  height_m: number;
  monthly_data: number[];
  annual_mean: number;
  unit: string;
}

export interface WindMonthlyRequest {
  /** Wind measurement height in meters (50 | 100 | 150 | 200) */
  height?: number;
  /** GeoJSON polygon of the selected area, or null for state-wide Pernambuco data */
  geometry: { type: string; coordinates: number[][][] } | null;
  /** Optional backend raster layer override */
  layer_name?: string;
}

/**
 * Fetch wind velocity series data from backend
 *
 * Retrieves monthly wind velocity measurements for a specific height and
 * optional selected area.
 * Backend pre-calculates annual_mean to ensure consistency across visualizations.
 *
 * @param payload - Request with height and optional selected area geometry
 * @returns Promise resolving to wind velocity data with statistics
 */
export const ApiGetWindSeries = async (
  payload: WindMonthlyRequest
): Promise<WindSeriesResponse> => {
  try {
    const response = await apiService.post(API_WIND_SERIES_ROUTE, payload);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Wind power density data response from backend
 *
 * Returns monthly wind power density with pre-calculated statistics.
 * Used by PowerDensityChart component to display power density trends.
 *
 * Backend calculates both annual_mean and total_accumulated to ensure
 * consistency across all visualizations and avoid frontend calculation drift.
 */
export interface WindDensityResponse {
  height_m: number;
  monthly_data: number[];
  annual_mean: number;
  total_accumulated: number;
  unit: string;
}

/**
 * Fetch wind power density data from backend
 *
 * @param payload - Request with height and optional selected area geometry
 * @returns Promise resolving to wind power density data with statistics
 */
export const ApiGetWindDensity = async (
  payload: WindMonthlyRequest
): Promise<WindDensityResponse> => {
  try {
    const response = await apiService.post(API_WIND_DENSITY_ROUTE, payload);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Wind — Shared Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Available wind potential scenarios.
 *
 * Each scenario maps to a different GeoServer binary raster layer that
 * defines which areas are considered viable for wind energy production:
 * - Teorico    → pepe-raster:teorico    (no restrictions applied)
 * - Geografico → pepe-raster:geografico (geographic constraints applied)
 * - Economico  → pepe-raster:economico  (only economically viable areas)
 */
export enum WindScenario {
  Teorico = "teorico",
  Geografico = "geografico",
  Economico = "economico",
}

// ─────────────────────────────────────────────────────────────────────────────
// Wind Distribution (selected area)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Request payload for wind speed distribution over a selected area.
 *
 * layer_name must be the GeoServer layer: "pepe-raster:windspeedtest"
 */
export interface WindDistributionRequest {
  /** GeoServer wind speed layer. Always "pepe-raster:windspeedtest" */
  layer_name: string;
  /** Wind measurement height in meters */
  height?: number;
  /** GeoJSON geometry of the selected area, or null for state-wide Pernambuco data */
  geometry: { type: string; coordinates: number[][][] } | null;
  /** Reference month 1-12 (optional — omit for annual average) */
  month?: number;
}

/**
 * Single bin of the wind speed distribution histogram.
 */
export interface WindDistributionBin {
  min: number;
  max: number;
  /** Percentage of the area that falls within this speed range (0–100) */
  percentage: number;
}

/**
 * Area statistics for wind speed within the selected geometry.
 */
export interface WindDistributionAreaStats {
  min: number;
  max: number;
  mean: number;
  std_dev: number;
}

/**
 * Full response from the wind distribution endpoint.
 */
export interface WindDistributionResponse {
  metadata: {
    layer: string;
    unit: string;
    area_km2: number;
  };
  stats: WindDistributionAreaStats;
  distribution: WindDistributionBin[];
}

/**
 * Fetch wind speed distribution for a selected area geometry.
 *
 * Endpoint: POST /api/v1/report/wind-distribution
 *
 * The backend receives the area geometry and a layer name derived from the
 * selected height, then returns the percentage of the area that falls within
 * each wind speed bin, plus area-wide statistics (min, max, mean, std_dev).
 *
 * @param payload - Request with layer_name, geometry, year, and optional month
 * @returns Promise resolving to distribution bins and area statistics
 */
export const ApiGetWindDistribution = async (
  payload: WindDistributionRequest
): Promise<WindDistributionResponse> => {
  try {
    const response = await apiService.post(
      API_WIND_DISTRIBUTION_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Request payload for wind power potential per speed bin.
 *
 * layer_name is derived from height: "wind_speed_100m", "wind_speed_150m", etc.
 * geometry = null returns state-wide data; geometry = GeoJSON returns area data.
 */
export interface WindPotentialRequest {
  /** Layer name derived from height. Example: "wind_speed_100m" */
  layer_name: string;
  /** Wind measurement height in meters (50 | 100 | 150 | 200) */
  height?: number;
  /** GeoJSON geometry of the selected area, or null for state-wide data */
  geometry: { type: string; coordinates: number[][][] } | null;
  /** Viability scenario that determines which GeoServer raster layer is used */
  scenario?: WindScenario;
}

/**
 * Single bin of the wind potential histogram.
 * Represents installed power potential for a wind speed range at a given height.
 */
export interface WindPotentialBin {
  min: number;
  max: number;
  /** Installable power for this speed bin (MW) */
  power_mw: number;
  /** Annual energy generation for this speed bin (GWh) */
  energy_gwh: number;
  /** Capacity factor: suitable area / total area (%) */
  capacity_factor: number;
}

/**
 * Full response from the wind potential endpoint.
 */
export interface WindPotentialResponse {
  metadata: {
    layer: string;
    unit: string;
    scenario: string;
    area_km2: number;
  };
  distribution: WindPotentialBin[];
  /** Sum of power_mw across all bins (MW) */
  total_accumulated_mw: number;
  /** Mean annual energy across all bins (GWh) */
  annual_mean_mw: number;
  /** Real annual energy (GWh) computed from wind power density raster; 0 if unavailable */
  annual_energy_gwh: number;
}

/**
 * Fetch wind power potential per speed bin for a given height and area.
 *
 * Endpoint: POST /api/v1/report/wind-potential
 *
 * The backend cross-references height (via layer_name) and wind speed bins
 * to return the installable power potential (MW) and annual energy (GWh)
 * for each speed range. If geometry is null, returns state-wide data.
 *
 * @param payload - Request with layer_name, geometry, scenario, and year
 * @returns Promise resolving to potential bins and summary statistics
 */
export const ApiGetWindPotential = async (
  payload: WindPotentialRequest
): Promise<WindPotentialResponse> => {
  try {
    const response = await apiService.post(API_WIND_POTENTIAL_ROUTE, payload);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Wind Cumulative (Potencial Eólico Cumulativo por Faixa de Velocidade)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Request payload for cumulative wind power potential.
 *
 * layer_name is derived from height: "wind_speed_100m", "wind_speed_150m", etc.
 * geometry = null returns state-wide data; geometry = GeoJSON returns area data.
 * Each bin represents all areas with wind speed >= that threshold.
 */
export interface WindCumulativeRequest {
  /** Layer name derived from height. Example: "wind_speed_100m" */
  layer_name: string;
  /** Wind measurement height in meters (50 | 100 | 150 | 200) */
  height?: number;
  /** GeoJSON geometry of the selected area, or null for state-wide data */
  geometry: { type: string; coordinates: number[][][] } | null;
  /** Viability scenario that determines which GeoServer raster layer is used */
  scenario?: WindScenario;
}

/**
 * Single bin of the cumulative wind potential.
 * Represents total installed power potential for all areas with
 * wind speed >= min threshold at a given height.
 */
export interface WindCumulativeBin {
  /** Lower speed threshold for this cumulative bin (m/s) */
  min: number;
  /** Cumulative installable power for all areas with speed >= min (MW) */
  power_mw: number;
  /** Cumulative annual energy for all areas with speed >= min (GWh) */
  energy_gwh: number;
  /** Capacity factor: suitable area / total area (%) */
  capacity_factor: number;
}

/**
 * Full response from the wind cumulative endpoint.
 */
export interface WindCumulativeResponse {
  metadata: {
    layer: string;
    unit: string;
    scenario: string;
    area_km2: number;
  };
  distribution: WindCumulativeBin[];
  /** Total accumulated power (MW) — same as distribution[0].power_mw */
  total_accumulated_mw: number;
  /** Mean annual energy across all bins (GWh) */
  annual_mean_mw: number;
}

/**
 * Fetch cumulative wind power potential per speed threshold.
 *
 * Endpoint: POST /api/v1/report/wind-cumulative
 *
 * The backend returns cumulative potential for each speed threshold,
 * where each bin includes all areas with wind speed >= that value.
 * If geometry is null, returns state-wide data.
 *
 * @param payload - Request with layer_name, geometry, scenario, and year
 * @returns Promise resolving to cumulative potential bins and summary statistics
 */
export const ApiGetWindCumulative = async (
  payload: WindCumulativeRequest
): Promise<WindCumulativeResponse> => {
  try {
    const response = await apiService.post(API_WIND_CUMULATIVE_ROUTE, payload);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface AreaPotentialOutput {
  stats: StatCard[];
}

// A API calcula sempre a partir da GHI da aeroespacial — não há camada/ano a escolher.
export const ApiGetAreaPotential = async (payload: {
  geometry: any;
}): Promise<AreaPotentialOutput> => {
  try {
    const response = await apiService.post(API_AREA_POTENTIAL_ROUTE, payload);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiGetWindAreaPotential = async (payload: {
  geometry: any;
  layer_name?: string;
}): Promise<AreaPotentialOutput> => {
  try {
    const response = await apiService.post(
      API_AREA_WIND_POTENTIAL_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export const ApiGetWindRose = async (
  payload: GetWindRoseParams
): Promise<WindRose> => {
  try {
    // POST (not GET) — geometry is a nested polygon, and the backend needs it in the
    // body to scope the wind rose to the selected area instead of always the whole state.
    const response = await apiService.post(API_WIND_ROSE_ROUTE, payload);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface RasterStatsPayload {
  layer: "ghi" | "windspeedtest";
  bbox: [number, number, number, number];
}

export interface RasterStatsResponse {
  min: number;
  max: number;
  mean: number;
  unit: string;
}

export const ApiGetRasterStats = async (
  payload: RasterStatsPayload
): Promise<RasterStatsResponse> => {
  try {
    const response = await apiService.post<RasterStatsResponse>(
      API_REPORT_RASTER_STATS_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface RegionAreaPayload {
  cd_rgi: string;
}

export interface RegionAreaResponse {
  cd_rgi: string;
  label: string;
  area_km2: number;
}

export const ApiGetRegionArea = async (
  payload: RegionAreaPayload
): Promise<RegionAreaResponse> => {
  try {
    const response = await apiService.post<RegionAreaResponse>(
      API_REPORT_REGION_AREA_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface LegendItemCountRequest {
  label: string;
  cql_filter: string | null;
  cd_rgi?: string;
}

export interface LegendLayerCountRequest {
  legend_id: string;
  geoserver_type_name: string;
  category: string;
  layer_type: "vector" | "raster" | "regioes_imediatas";
  raster_id?: "ghi" | "windspeedtest";
  is_point_geometry?: boolean;
  count_group_by_field?: string;
  items: LegendItemCountRequest[];
}

export interface LegendCountsPayload {
  geometry: import("geojson").Geometry | null;
  is_area_report: boolean;
  layers: LegendLayerCountRequest[];
}

interface LegendRangeResult {
  min: number;
  max: number;
  mean: number;
  unit: string;
}

interface LegendItemCountResult {
  label: string;
  count?: number;
  area_pct?: number;
  area_km2?: number;
  range?: LegendRangeResult;
}

interface LegendLayerCountResult {
  legend_id: string;
  items: LegendItemCountResult[];
}

export interface LegendCountsResponse {
  layers: LegendLayerCountResult[];
}

export const ApiGetLegendCounts = async (
  payload: LegendCountsPayload
): Promise<LegendCountsResponse> => {
  try {
    const response = await apiService.post<LegendCountsResponse>(
      API_REPORT_LEGEND_COUNTS_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface GenerationSourceSummary {
  source_type: string;
  layer_name: string;
  count: number;
  total_kw: number;
  total_mw: number;
}

export interface GenerationPowerSummaryResponse {
  sources: GenerationSourceSummary[];
  grand_total_kw: number;
  grand_total_mw: number;
}

export const ApiGetGenerationPowerSummary = async (
  geometry: GeoJSON.Geometry
): Promise<GenerationPowerSummaryResponse> => {
  try {
    const response = await apiService.post<GenerationPowerSummaryResponse>(
      API_GENERATION_POWER_SUMMARY_ROUTE,
      { geometry }
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export interface ProjectionAreaStatsResponse {
  propitious_area_km2: number;
  total_area_km2: number;
  percentage: number;
  installable_gw: number;
  annual_energy_gwh: number;
  // Exact vectorized shape of the propitious area within the selected polygon —
  // propitious_area_km2 is its geodesic area, so the two are always consistent.
  // null for the whole-state case or when no propitious pixels were found.
  propitious_geometry?: GeoJSON.Geometry | null;
  /** Memorial de Cálculo da Geração — as premissas efetivamente usadas acima. */
  generation_memorial?: GenerationMemorial | null;
}

/** Uma premissa do Memorial de Cálculo da Geração. */
export interface MemorialPremise {
  id: string;
  label: string;
  value: number | string;
  unit: string;
  source: string;
}

/** Um passo da cadeia área propícia → potencial instalável → energia anual. */
export interface MemorialStep {
  id: string;
  label: string;
  /** Já formatada em pt-BR pelo backend. */
  formula: string;
  value: number;
  unit: string;
}

export interface GenerationMemorial {
  energy: "solar" | "eolico";
  method: string;
  premises: MemorialPremise[];
  steps: MemorialStep[];
  /** true quando o recurso medido faltou e o cálculo usou um valor regional. */
  fallback_used: boolean;
  notes: string[];
}

export const ApiGetProjectionAreaStats = async (payload: {
  layer_name: string;
  geometry?: GeoJSON.Geometry | null;
  hub_height_m?: number;
}): Promise<ProjectionAreaStatsResponse> => {
  try {
    const response = await apiService.post<ProjectionAreaStatsResponse>(
      API_PROJECTION_AREA_STATS_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/** Uma restrição no Memorial de Cálculo da Área Propícia. */
export interface RestrictionEntry {
  id: string;
  label: string;
  description: string;
  /** Parcela EXCLUSIVA pela qual a restrição responde, em % da área selecionada.
   * Terreno atingido por mais de uma restrição conta só para a de maior
   * incidência, então este valor é sempre <= incidence_percentage. */
  percentage: number;
  area_km2: number;
  /** Incidência independente: quanto a restrição atinge ignorando as demais. */
  incidence_percentage: number;
  /** false quando a máscara não pôde ser lida — a área dela cai em não-atribuído. */
  available: boolean;
  /**
   * Forma da parcela atribuída a esta restrição, recortada na área selecionada.
   * Corresponde a `percentage`, não à incidência. Ausente quando a restrição não
   * incide sobre a área ou sua máscara está indisponível.
   */
  geometry?: GeoJSON.Geometry | null;
}

export interface RestrictionBreakdownResponse {
  level: string;
  level_label: string;
  energy: "solar" | "eolico";
  total_area_km2: number;
  propitious_area_km2: number;
  propitious_percentage: number;
  /** Todas as restrições em vigor no nível: as próprias e as herdadas da cascata. */
  restrictions: RestrictionEntry[];
  /** Sobra que a atribuição não explica. Acima de 1% indica máscara ausente. */
  unattributed_percentage: number;
  unattributed_km2: number;
  residual_normalized: boolean;
  unavailable_restriction_ids: string[];
  warning?: string | null;
}

export const ApiGetRestrictionBreakdown = async (payload: {
  layer_name: string;
  /** Omitida: Pernambuco continental inteiro, em grade reduzida. */
  geometry?: GeoJSON.Geometry | null;
  hub_height_m?: number;
  /** false dispensa a geometria de cada parcela (o PDF não desenha). */
  include_geometry?: boolean;
}): Promise<RestrictionBreakdownResponse> => {
  try {
    const response = await apiService.post<RestrictionBreakdownResponse>(
      API_RESTRICTION_BREAKDOWN_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

// ─── Caracterização do recurso (dados aeroespaciais) ──────────────────────────

export type ResourceRegion = "pe" | "noronha";

export interface ResourceHistogramBin {
  start: number;
  end: number;
  /** Percentual dos pixels da área nesta faixa. */
  percentage: number;
}

export interface ResourceVariable {
  id: string;
  label: string;
  group: "solar" | "eolico" | "terreno";
  unit: string;
  description: string;
  source: string;
  available: boolean;
  mean?: number | null;
  min?: number | null;
  max?: number | null;
  p10?: number | null;
  p90?: number | null;
  histogram: ResourceHistogramBin[];
}

export interface ResourceCharacterizationResponse {
  region: ResourceRegion;
  energy: "solar" | "eolico";
  wind_height_m: number;
  variables: ResourceVariable[];
  wind_rose: Array<{ direction_deg: number; frequency_percentage: number }>;
  interannual: Array<{ year: number; index: number }>;
  land_use: Array<{ value: number; label: string; percentage: number }>;
  unavailable: string[];
}

export const ApiGetResourceCharacterization = async (payload: {
  energy: "solar" | "eolico";
  geometry?: GeoJSON.Geometry | null;
  region?: ResourceRegion;
}): Promise<ResourceCharacterizationResponse> => {
  try {
    const response = await apiService.post<ResourceCharacterizationResponse>(
      API_RESOURCE_CHARACTERIZATION_ROUTE,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};
